import { useEffect, useLayoutEffect } from 'react';

// externals
import { useDispatch, useSelector } from 'react-redux';
import type { SelectInputOption } from '@interstate/components/SelectInput';

// libraries
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
// components
import { SelectInput } from './TradeInSelectInput';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// constants/enums
import { TRADE_IN_MAKE } from '@makemydeal/dr-dash-components';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

interface MakeFieldProps {
    isManualFormOpen: boolean;
    disabled?: boolean;
}

const MakeField: React.FC<MakeFieldProps> = ({ isManualFormOpen, disabled }) => {
    const { tradeInIndex, tradeInValidator, tradeInFormErrors } = useSingleTradeInFormProvider();
    const dispatch = useDispatch();
    const vehicleYear = useSelector(tradeInsSelectors.getTradeYear(tradeInIndex));
    const vehicleMake = useSelector(tradeInsSelectors.getMake(tradeInIndex));
    const vehicleMakeId = useSelector(tradeInsSelectors.getTradeMakeId(tradeInIndex));
    const tradeInVehicleMakes = useSelector(tradeInsSelectors.getTradeInComponentMakes(tradeInIndex));
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));
    const vinHasBeenDecoded = useSelector(tradeInsSelectors.vinHasBeenDecoded(tradeInIndex));
    const hasMultipleVin = useSelector(tradeInsSelectors.hasMultipleVin(tradeInIndex));
    const makeValue = Number(vehicleYear) === 0 || vehicleMake === 'default' ? '' : vehicleMake;
    const normalizedMakeValue =
        tradeInVehicleMakes.options.find((option) => makeValue?.toLowerCase() === option.name.toLowerCase())?.name ?? makeValue;
    const isFieldDisabled =
        hasMultipleVin || (tradeInVehicleMakes.options.length === 0 && !makeValue) || !Number(vehicleYear) || disabled;

    useLayoutEffect(() => {
        tradeInValidator.setValue('vehicle.make', makeValue || '', { validate: true, clearErrors: false });
    }, [makeValue, tradeInValidator]);

    useEffect(() => {
        const make = tradeInVehicleMakes.options.find((option) => vehicleMake?.toLowerCase() === option.name.toLowerCase());

        if (make) {
            dispatch(tradeInsActionCreators.updateTradeInMakeId(make.id, tradeInIndex));
        }
    }, [dispatch, tradeInVehicleMakes, vehicleMake]);

    useEffect(() => {
        if (vinHasBeenDecoded || (vehicleYear && vehicleMake && vehicleMakeId)) {
            const fetchModels = () => {
                dispatch(
                    tradeInsActionCreators.fetchTradeInComponentModels(
                        MANAGER_VIEW_APP_PREFIX,
                        vehicleYear,
                        vehicleMakeId,
                        tradeInIndex
                    )
                );
            };

            if (vehicleMakeId) {
                fetchModels();
            }
        }
    }, [vinHasBeenDecoded, vehicleYear, vehicleMake, vehicleMakeId, dispatch]);

    const getTradeInMakes = (): SelectInputOption[] => {
        const makesOptions =
            makeValue && !tradeInVehicleMakes.options.find((make) => make.name.toLowerCase() === makeValue.toLowerCase())
                ? [...tradeInVehicleMakes.options, { name: makeValue }]
                : tradeInVehicleMakes.options;

        return makesOptions.map((make) => ({ label: make.name, value: make.name })).sort();
    };

    const onMakeChange = (event: any) => {
        const make = tradeInVehicleMakes.options.find((make) => make.name === event.target.value);
        if (!make) return;
        const updatedVehicle = {
            ...tradeInVehicle,
            make,
            model: { id: '', name: '' },
            trim: { id: '', name: '', vehicleId: '' }
        };

        tradeInValidator.setValue('vehicle.make', make.name, { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.model', '', { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.trim', '', { validate: true, clearErrors: false });
        tradeInValidator.commit(['vehicle.make', 'vehicle.model', 'vehicle.trim'], { validate: true });

        dispatch(tradeInsActionCreators.updateTradeInMake(make, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle(updatedVehicle, tradeInIndex));
    };

    return (
        <>
            {isManualFormOpen && (
                <SelectInput
                    id="trade-makes-select-input"
                    data-testid="trade-makes-select-input"
                    label={TRADE_IN_MAKE}
                    name={TRADE_IN_MAKE}
                    onChange={onMakeChange}
                    options={getTradeInMakes()}
                    value={normalizedMakeValue}
                    placeholder="Select"
                    disabled={isFieldDisabled}
                    displayDeselectOption={false}
                    size="sm"
                    hasError={tradeInFormErrors.vehicle.make.hasError}
                    errorMessage={tradeInFormErrors.vehicle.make.errorMessage}
                />
            )}
        </>
    );
};

export default MakeField;
